import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { pillarsAPI } from '../services/api';
import OptimizedImage from './OptimizedImage';
import { usePageLoading } from '../hooks/usePageLoading';
import { useConfirmModal } from '../hooks/useConfirmModal';
import ConfirmModal from './ConfirmModal';
import LoadingOverlay from './LoadingOverlay';
import { getImageSource, handleImageError } from '../utils/imageUtils';
import './PillarHierarchy.css';

// Extend Window interface for global functions
declare global {
  interface Window {
    showToast: (message: string, type?: 'success' | 'error' | 'info' | 'warning', duration?: number) => void;
  }
}

interface HierarchyNode {
  _id: string;
  name: string;
  level: number;
  image?: string;
  imageUrl?: string;
  L1pillarImage?: string;
  L1pillarImageUrl?: string;
  article?: string;
  children: HierarchyNode[];
}

interface L1PillarData {
  _id: string;
  name: string;
  image?: string;
  imageUrl?: string;
  L1pillarImage?: string;
  L1pillarImageUrl?: string;
  article?: string;
}

interface HierarchyData {
  l1Pillar: L1PillarData;
  hierarchy: HierarchyNode[];
  totalPillars: number;
}

interface LevelCount {
  level: number;
  count: number;
}

const PillarHierarchy: React.FC = () => {
  const { l1Name } = useParams<{ l1Name: string }>();
  const navigate = useNavigate();
  const [hierarchyData, setHierarchyData] = useState<HierarchyData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [levelCounts, setLevelCounts] = useState<LevelCount[]>([]);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageKey, setImageKey] = useState(0);

  const { modalState, showConfirm, handleConfirm, handleCancel: handleModalCancel } = useConfirmModal();

  // Reset hover state when modal opens
  useEffect(() => {
    setHoveredNode(null);
  }, []);

  // Use page loading hook - wait for hierarchy data to load
  const { isLoading: pageLoading } = usePageLoading({
    dependencies: [hierarchyData !== null],
    onLoadComplete: () => {
      console.log('✅ Pillar hierarchy loaded successfully');
    }
  });

  // Calculate level counts from hierarchy data
  const calculateLevelCounts = (nodes: HierarchyNode[]): LevelCount[] => {
    const counts: { [key: number]: number } = {};
    
    const countNodes = (nodeList: HierarchyNode[]) => {
      nodeList.forEach(node => {
        // Skip L1 pillar (level 1) from the count since it's the root
        if (node.level > 1) {
          counts[node.level] = (counts[node.level] || 0) + 1;
        }
        if (node.children && node.children.length > 0) {
          countNodes(node.children);
        }
      });
    };
    
    countNodes(nodes);
    
    return Object.entries(counts)
      .map(([level, count]) => ({ level: parseInt(level), count }))
      .sort((a, b) => a.level - b.level);
  };

  useEffect(() => {
    const fetchHierarchy = async () => {
      if (!l1Name) return;
      
      try {
        const response = await pillarsAPI.getHierarchy(l1Name);
        setHierarchyData(response);
        
        // Calculate level counts
        const counts = calculateLevelCounts(response.hierarchy);
        setLevelCounts(counts);
        
        // Expand the root node by default
        setExpandedNodes(new Set([response.l1Pillar._id]));
        
        // Force image refresh for new data
        const newKey = Date.now();
        setImageKey(newKey);
        setImageLoading(true);
        
        // Reset loading state after a short delay
        setTimeout(() => {
          setImageLoading(false);
        }, 100);
      } catch (err) {
        setError('Failed to load hierarchy');
        console.error('Error fetching hierarchy:', err);
      }
    };

    fetchHierarchy();
  }, [l1Name]);

  const handleDeletePillar = async (node: HierarchyNode) => {
    // Don't allow deletion of L1 pillar
    if (node._id === hierarchyData?.l1Pillar._id) {
      if (typeof window.showToast === 'function') {
        window.showToast('Cannot delete the root L1 pillar.', 'warning');
      }
      return;
    }
    
    // Find children of this node to inform the user
    const findChildren = (nodes: HierarchyNode[], targetId: string): HierarchyNode[] => {
      let children: HierarchyNode[] = [];
      for (const n of nodes) {
        if (n._id === targetId) {
          children = n.children || [];
          break;
        }
        if (n.children) {
          children = children.concat(findChildren(n.children, targetId));
        }
      }
      return children;
    };
    
    const children = findChildren(hierarchyData?.hierarchy || [], node._id);
    const hasChildren = children.length > 0;
    
    // Create appropriate confirmation message
    let confirmMessage = `Are you sure you want to delete "${node.name}"?`;
    if (hasChildren) {
      confirmMessage += `\n\nThis will reassign ${children.length} child pillar${children.length > 1 ? 's' : ''} to the next parent in the hierarchy.`;
    }
    
    // Use React confirm modal
    const confirmDelete = await showConfirm(confirmMessage, { type: 'warning' });
    
    if (!confirmDelete) return;
    
    try {
      // Show processing message
      if (typeof window.showToast === 'function') {
        window.showToast(`Deleting "${node.name}" and reassigning children...`, 'info');
      }
      
      await pillarsAPI.delete(node._id);
      
      // Refresh the hierarchy data
      const response = await pillarsAPI.getHierarchy(l1Name!);
      setHierarchyData(response);
      
      // Recalculate level counts
      const counts = calculateLevelCounts(response.hierarchy);
      setLevelCounts(counts);
      
      // Show success toast with details
      if (typeof window.showToast === 'function') {
        if (hasChildren) {
          window.showToast(`"${node.name}" deleted successfully! ${children.length} child pillar${children.length > 1 ? 's' : ''} reassigned.`, 'success');
        } else {
          window.showToast(`"${node.name}" deleted successfully!`, 'success');
        }
      }
    } catch (error) {
      console.error('Failed to delete pillar:', error);
      if (typeof window.showToast === 'function') {
        window.showToast('Failed to delete pillar.', 'error');
      }
    }
  };

  const toggleNode = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const renderNode = (node: HierarchyNode, depth: number = 0) => {
    const isExpanded = expandedNodes.has(node._id);
    const hasChildren = node.children && node.children.length > 0;
    const isL1Pillar = node._id === hierarchyData?.l1Pillar._id;
    const isHovered = hoveredNode === node._id;

    // Use standardized image loading - hierarchy nodes now have both 'image' (R2 key) and 'imageUrl' (signed URL)
    const nodeImageSrc = getImageSource(node, 'pillar', imageKey);

    return (
      <div 
        key={node._id} 
        className="hierarchy-node"
        onMouseEnter={() => setHoveredNode(node._id)}
        onMouseLeave={() => setHoveredNode(null)}
      >
        <div 
          className={`node-content ${hasChildren ? 'has-children' : ''}`}
          style={{ 
            // paddingLeft: `${depth * 20}px`,
            position: 'relative'
          }}
        >
          {/* Background image with proper loading using OptimizedImage */}
          {nodeImageSrc && (
            <div 
              className="node-background-image"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                overflow: 'hidden'
              }}
            >
              <OptimizedImage
                src={nodeImageSrc}
                alt={`${node.name} background`}
                className="node-background-img"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  opacity: imageLoading ? 0.5 : 1,
                  transition: 'opacity 0.3s ease'
                }}
                onLoad={() => {
                  setImageLoading(false);
                }}
                onError={() => handleImageError(setImageLoading, false)}
                dataAttributes={{
                  'data-image-key': node.image || '',
                  'data-pillar-id': node._id
                }}
              />
            </div>
          )}
          
          <div className="node-content-right">
            <div className="node-header">
              {hasChildren && (
                <button 
                  className={`expand-button ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => toggleNode(node._id)}
                >
                  {isExpanded ? '▼' : '▶'}
                </button>
              )}
              
              <div className="node-info">
                <div className="node-level">L{node.level}</div>
                <div className="node-name">{node.name}</div>
              </div>

              {/* Delete button - only show on hover and not for L1 pillar */}
              {isHovered && !isL1Pillar && (
                <button
                  className="node-delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeletePillar(node);
                  }}
                  title={`Delete ${node.name}`}
                >
                  ×
                </button>
              )}
            </div>
            
            {node.article && (
              <div className="node-article">
                {node.article.length > 100 
                  ? `${node.article.substring(0, 100)}...` 
                  : node.article
                }
              </div>
            )}
          </div>
          
          {/* Loading overlay for node images */}
          <LoadingOverlay isLoading={imageLoading} />
        </div>
        
        {hasChildren && isExpanded && (
          <div className="node-children">
            {node.children.map(child => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderLevelDashboard = () => {
    if (levelCounts.length === 0) return null;

    return (
      <div className="level-dashboard">
        <h3>Pillar Distribution by Level</h3>
        <div className="dashboard-grid">
          {levelCounts.map(({ level, count }) => (
            <div key={level} className="level-card">
              <div className="level-number">L{level}</div>
              <div className="level-count">{count}</div>
              <div className="level-label">
                {count === 1 ? 'Pillar' : 'Pillars'}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (error || !hierarchyData) {
    return (
      <div className="hierarchy-container">
        <div className="error">
          {error || 'Failed to load hierarchy'}
          <button onClick={() => navigate('/pillars')} className="back-button">
            Back to Pillars
          </button>
        </div>
      </div>
    );
  }

  // Calculate total from level counts for consistency
  const totalFromLevelCounts = levelCounts.reduce((sum, { count }) => sum + count, 0);

  return (
    <div className="hierarchy-container">
      {/* Loading Overlay */}
      <LoadingOverlay isLoading={pageLoading} message="Loading hierarchy..." />
      
      <div className="hierarchy-header">
        <button onClick={() => navigate('/pillars')} className="back-button">
          ← Back to Pillars
        </button>
        <h1>{hierarchyData.l1Pillar.name} Hierarchy</h1>
        <div className="hierarchy-stats">
          Total Pillars: {totalFromLevelCounts}
        </div>
      </div>

      <div className="l1-pillar-section">
        <div className="l1-pillar-card">
          <div className="l1-pillar-image-wrapper">
            <OptimizedImage
              src={getImageSource(hierarchyData.l1Pillar, 'pillar', imageKey)}
              alt={hierarchyData.l1Pillar.name}
              className="l1-pillar-main-image"
              onLoad={() => {
                setImageLoading(false);
              }}
              onError={() => handleImageError(setImageLoading, false)}
              dataAttributes={{
                'data-image-key': hierarchyData.l1Pillar.image || '',
                'data-pillar-id': hierarchyData.l1Pillar._id
              }}
            />
            <LoadingOverlay isLoading={imageLoading} />
          </div>
          {renderLevelDashboard()}
        </div>
      </div>

      <div className="hierarchy-tree">
        <h3>Pillar Tree</h3>
        {hierarchyData.hierarchy.map(node => renderNode(node))}
      </div>

      {/* Element Preview Panel */}
      {/* {showElementPreview && (
        <ElementPreviewPanel
          isOpen={showElementPreview}
          onClose={() => setShowElementPreview(false)}
        />
      )} */}

      {/* Confirm Modal */}
      {modalState && (
        <ConfirmModal
          isOpen={modalState.isOpen}
          message={modalState.message}
          type={modalState.type}
          okText={modalState.okText}
          cancelText={modalState.cancelText}
          onConfirm={handleConfirm}
          onCancel={handleModalCancel}
        />
      )}
    </div>
  );
};

export default PillarHierarchy; 